import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, Subscription, Observable } from 'rxjs';

import {
    PRODUCT_ICONS,
    PRODUCT_COLORS,
} from '../../../../../constants/productMappings';
import {
    loadRoutingGuide,
    loadRoutingGuideItems,
    deleteRoutingGuideItem,
} from '../../../store/actions/routing-guide.actions';
import {
    RoutingGuideState,
    RoutingGuideStateItemsInfo,
} from '../../../store/schedules.state';
import {
    RoutingGuideItem,
    RoutingGuide,
} from '../../../store/models/routing-guide.models';
import { AppState } from '../../../../../store/app.state';
import { Provider } from '../../../store/models/providers.models';
import { fadeInAnimation } from '../../../../../helpers/reusableAnimations';
import { selectRoutingGuideState } from '../../../store/selectors/routing-guide.selectors';
import { AlgoliaLocationsService } from '../../../../shared/services/algolia-locations.service';
import { UploadRoutingGuideComponent } from '../upload-routing-guide/upload-routing-guide.component';
import { DownloadRoutingGuideComponent } from '../download-routing-guide/download-routing-guide.component';
import { SimulateRoutingGuideComponent } from '../simulate-routing-guide/simulate-routing-guide.component';
import { NewRoutingGuideItemModalComponent } from '../new-routing-guide-item-modal/new-routing-guide-item-modal.component';
import { UpdateRoutingGuideItemModalComponent } from '../update-routing-guide-item-modal/update-routing-guide-item-modal.component';

@Component({
    selector: 'app-routing-guide',
    templateUrl: './routing-guide.component.html',
    styleUrls: ['./routing-guide.component.scss'],
    animations: [fadeInAnimation],
    providers: [DialogService],
})
export class RoutingGuideComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store<AppState>);
    private readonly modalService = inject(DialogService);
    private readonly algoliaLocations = inject(AlgoliaLocationsService);

    product$: BehaviorSubject<string> = new BehaviorSubject('');
    routingGuideState$: Observable<RoutingGuideState> = this.store.select(
        selectRoutingGuideState
    );
    productIcons = PRODUCT_ICONS;
    productColors = PRODUCT_COLORS;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    searchLocationResults: any[];
    itemsInfo: RoutingGuideStateItemsInfo;
    routingGuideItems: RoutingGuideItem[] = [];
    providers: Provider[];

    clonedRoutingGuideItems: RoutingGuideItem[] = [];
    productSubscription: Subscription;
    routingGuideStateSubscription: Subscription;
    routingGuide: RoutingGuide | null;
    isLoading = false;

    private _page = 1;
    pageSize = 20;
    totalItems: number | undefined = 0;
    filterSet = null;

    ngOnInit() {
        // Set a subscription to observe the routing guide state
        this.routingGuideStateSubscription = this.routingGuideState$.subscribe(
            state => {
                this.isLoading =
                    state.loadingRoutingGuide || state.deletingItem != null;
                this.routingGuide = state.routingGuide;
                this.providers = state.providers;
                this.itemsInfo = state.itemsInfo;
                this.routingGuideItems = this.itemsInfo.data;

                // Only get the currently requested page
                if (this.itemsInfo && this.itemsInfo.meta) {
                    this.totalItems = this.itemsInfo.meta.total;
                }
            }
        );

        // If the product changes, we'll request for a new routing guide
        this.productSubscription = this.product$.subscribe(product => {
            if (product) {
                this.store.dispatch(
                    loadRoutingGuide({ product, pageSize: this.pageSize })
                );
            }
        });
    }

    ngOnDestroy(): void {
        // Unsubscribe all subscriptions
        if (this.productSubscription) {
            this.productSubscription.unsubscribe();
        }

        if (this.routingGuideStateSubscription) {
            this.routingGuideStateSubscription.unsubscribe();
        }
    }

    /**
     * Handles the filter from routing-guide-search being changed.
     * @param event The event data containing the filter information
     */
    onFilterChanged(event: {
        origin: { locode: string };
        destination: { locode: string };
        provider: { id: number };
    }) {
        this.filterSet = null;
        if (!event) {
            this._page = 1;
        } else {
            this.filterSet = event;
        }
        this.loadRoutingGuideItemsLazy();
    }

    /**
     * Provides the trackBy item for the table.
     */
    routingGuideTrackBy = (_, item) => {
        return item._id;
    };

    /**
     * Gets current page
     */
    get page() {
        return this._page;
    }

    /**
     * Sets current page
     */
    set page(page: number) {
        // Don't do all this stuff if the page number doesn't change
        if (page !== this._page) {
            this._page = page;
            this.loadRoutingGuideItemsLazy();
        }
    }

    /**
     * Handles lazy loading of paged items
     */
    loadRoutingGuideItemsLazy() {
        this.store.dispatch(
            loadRoutingGuideItems({
                routingGuideId: this.routingGuide?.id,
                page: this.page,
                pageSize: this.pageSize,
                origin: this.filterSet?.origin ? this.filterSet?.origin.locode : null,
                destination: this.filterSet?.destination ? this.filterSet?.destination.locode : null,
                provider: this.filterSet?.provider ? this.filterSet?.provider.id : null,
            })
        );
    }

    /**
     * Hamdles searching for locations
     * @param event the event emitted by the typeahead
     */
    searchLocation(event) {
        const searchSubscription = this.algoliaLocations
            .getLocationSuggestions(event.query)
            .subscribe(results => {
                this.searchLocationResults = results;
                searchSubscription.unsubscribe();
            });
    }

    /**
     * Shows the dialog for adding a new routing guide item.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addRoutingGuideItem(rowData: any | null = null) {
        const modalRef = this.modalService.open(NewRoutingGuideItemModalComponent, {
                position: 'center',
                showHeader: false,
                closable: false,
                width: '400px',
                contentStyle: {
                    padding: 0,
                },
                data: {
                    rowData,
                },
            }
        );

        modalRef.onClose.subscribe(res => {
            if (res) {
                // We want to go to the first page so the new item is in view
                this.page = 1;
            }
        });
    }

    /**
     * Shows the dialog for updating a routing guide item
     * @param routingGuideItem the routing guide item to be updated.
     */
    updateRoutingGuideItem(routingGuideItem: RoutingGuideItem) {
        const modalRef = this.modalService.open(UpdateRoutingGuideItemModalComponent, {
            position: 'center',
            showHeader: false,
            closable: false,
            width: '50%',
            contentStyle: {
                padding: 0,
            }
        });

        if (routingGuideItem) {
            const dialogRef =
                this.modalService.dialogComponentRefMap.get(modalRef);
            dialogRef.changeDetectorRef.detectChanges();
            const instance = dialogRef.instance.componentRef
                .instance as UpdateRoutingGuideItemModalComponent;
            instance.routingGuideItem = routingGuideItem;
        }
    }

    /**
     * Deletes routing guide item from the server
     * @param routingGuideItem the routing guide item to be deleted
     * @param op the overlay panel instance to close
     */
    deleteRoutingGuideItem(routingGuideItem: RoutingGuideItem, op) {
        this.store.dispatch(deleteRoutingGuideItem({ routingGuideItem }));
        op.hide();
    }

    openDownloadRoutingGuideModal() {
        this.modalService.open(DownloadRoutingGuideComponent, {
            position: 'center',
            showHeader: false,
            closable: false,
            width: '50%',
            contentStyle: {
                padding: 0,
            },
        });
    }

    openUploadRoutingGuideModal() {
        this.modalService.open(UploadRoutingGuideComponent, {
            position: 'center',
            showHeader: false,
            closable: false,
            width: '50%',
            contentStyle: {
                padding: 0,
            },
        });
    }

    openSimulationModal() {
        const modalRef = this.modalService.open(SimulateRoutingGuideComponent, {
            position: 'center',
            showHeader: false,
            closable: true,
            width: '50%',
            closeOnEscape: true,
            dismissableMask: true,
            contentStyle: {
                padding: 0,
            },
        });

        if (this.routingGuide) {
            const dialogRef =
                this.modalService.dialogComponentRefMap.get(modalRef);
            dialogRef.changeDetectorRef.detectChanges();
            const instance = dialogRef.instance.componentRef
                .instance as SimulateRoutingGuideComponent;
            instance.setProduct(this.routingGuide.product);
        }
    }
}
